/**
 * iter129 — Applications client + offline queue.
 *
 * - Caches the user's pending application status in localStorage so the
 *   "Application Pending" pill renders instantly (offline-first).
 * - When the user submits an application offline, the payload is queued
 *   under `habano.pending_application` and flushed automatically when
 *   network returns.
 */
import { api } from "./api";

const KEY_STATUS = "habano.application_status";          // {kind, status, submitted_at}
const KEY_PENDING = "habano.pending_application";        // {endpoint, payload, queued_at}

const safeJSON = (raw, fallback = null) => {
  try { return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
};

export function readCachedApplicationStatus() {
  return safeJSON(localStorage.getItem(KEY_STATUS), null);
}

export function writeCachedApplicationStatus(s) {
  if (s) localStorage.setItem(KEY_STATUS, JSON.stringify(s));
  else localStorage.removeItem(KEY_STATUS);
}

/** Fetch & cache. Returns the cached value immediately when offline. */
export async function loadMyApplications() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return readCachedApplicationStatus();
  }
  try {
    const { data } = await api.get("/applications/my");
    const newest = Array.isArray(data) && data.length > 0 ? data[0] : null;
    const summary = newest ? {
      id: newest.id, kind: newest.kind, status: newest.status,
      submitted_at: newest.submitted_at,
    } : null;
    writeCachedApplicationStatus(summary);
    return summary;
  } catch {
    return readCachedApplicationStatus();
  }
}

/** Read the queued offline submission, if any. */
export function readPendingApplication() {
  return safeJSON(localStorage.getItem(KEY_PENDING), null);
}

export function clearPendingApplication() {
  localStorage.removeItem(KEY_PENDING);
}

function queuePendingApplication(endpoint, payload) {
  localStorage.setItem(KEY_PENDING, JSON.stringify({
    endpoint, payload, queued_at: new Date().toISOString(),
  }));
}

/**
 * Submit an application. When offline, the payload is queued locally and
 * flushed by `flushPendingApplication()` once the device reconnects.
 *
 * @param {"business"|"vendor"} kind
 * @returns {Promise<{ok:boolean, queued?:boolean, application?:object, error?:string}>}
 */
export async function submitApplication(kind, payload) {
  const endpoint = kind === "business" ? "/applications/business" : "/applications/vendor";
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    queuePendingApplication(endpoint, payload);
    writeCachedApplicationStatus({
      kind, status: "pending", submitted_at: new Date().toISOString(), queued: true,
    });
    return { ok: true, queued: true };
  }
  try {
    const { data } = await api.post(endpoint, payload);
    if (data?.application) {
      writeCachedApplicationStatus({
        id: data.application.id, kind: data.application.kind,
        status: data.application.status, submitted_at: data.application.submitted_at,
      });
    }
    // iter202 — Surface the auto-approve outcome so the Thank-You screen
    // can pick the right copy ("You're live!" vs "We'll review").
    return {
      ok: true,
      application: data?.application,
      auto_approved: !!data?.auto_approved,
      approval_type: data?.approval_type || null,
    };
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || e.message };
  }
}

/** Called by App.js on `online` event — flushes a queued submission. */
export async function flushPendingApplication() {
  const queued = readPendingApplication();
  if (!queued) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  try {
    const { data } = await api.post(queued.endpoint, queued.payload);
    if (data?.application) {
      writeCachedApplicationStatus({
        id: data.application.id, kind: data.application.kind,
        status: data.application.status, submitted_at: data.application.submitted_at,
      });
    }
    clearPendingApplication();
  } catch {
    // Keep it queued — try again next online event.
  }
}

/** List the caller's owned businesses (one-vs-many dashboard logic). */
const KEY_BIZ = "habano.my_businesses";
export async function loadMyBusinesses() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return safeJSON(localStorage.getItem(KEY_BIZ), []);
  }
  try {
    const { data } = await api.get("/me/businesses");
    const list = Array.isArray(data) ? data : [];
    localStorage.setItem(KEY_BIZ, JSON.stringify(list));
    return list;
  } catch {
    return safeJSON(localStorage.getItem(KEY_BIZ), []);
  }
}
